import type { JobTitle } from '@factoryfixinc/ats-interfaces';

import { useJobTitleStore } from './job-title.store';

import { useFFRestApi } from '@/composables/useApi';
import { arrayToRecord } from '@/utils/objects/array-to-record.util';
import { JobTitlePersistence } from './job-title.persistence';
import { InternalError } from '@/core/shared/errors/internal.error';

export default class JobTitleService {
  private jobTitlePersistence = new JobTitlePersistence();
  private store = useJobTitleStore();

  public get jobTitles(): JobTitle[] {
    return this.store.jobTitles;
  }

  public get jobTitlesRecord(): Record<number, JobTitle> {
    return arrayToRecord(this.store.jobTitles, 'id');
  }

  public async fetchJobTitles(): Promise<JobTitle[]> {
    const { data, error, statusCode } = await useFFRestApi(`/job-title`).json<JobTitle[]>();

    // Carry the numeric status: over HTTP/2 statusText is always empty, so a
    // stringified error would classify a genuine 403 as a transient outage.
    if (error.value || !data.value) {
      throw new InternalError('Could not load job titles', {
        error: error.value,
        status: statusCode.value,
      });
    }

    const jobTitles = data.value;

    jobTitles.sort((a, b) => (a.title ?? '').localeCompare(b.title ?? ''));
    this.store.jobTitles = jobTitles;

    return jobTitles;
  }

  public async normalizeJobTitle(displayTitle: string): Promise<JobTitle | null> {
    return this.jobTitlePersistence.normalizeJobTitle(displayTitle);
  }
}
